import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiError, api, get, qs, type FileRef } from "../lib/api.ts";
import { choiceColor, choicesOf, formatNumber, formatValue, isNumeric } from "../lib/format.ts";
import type { BlockDef, CollectionDef, FieldDef, ListResult, RecordView, WorkflowDef, WorkflowRun } from "../lib/types.ts";
import { FileChip, FieldInput } from "./field-input.tsx";

/**
 * The block renderer: the same components in the app and in the builder.
 * Blocks of one page share a runtime: which row each table has selected,
 * and a counter that makes every block reload after a write.
 */
interface Runtime {
  collections: CollectionDef[];
  /** Manual workflows shown as buttons on a record (none in the builder). */
  workflows: WorkflowDef[];
  selected: Record<string, string>;
  select(blockId: string, recordId: string): void;
  version: number;
  changed(): void;
}

const RuntimeCtx = createContext<Runtime | null>(null);
const useRuntime = () => useContext(RuntimeCtx)!;

export function PageRuntime({ collections, workflows = [], children }: { collections: CollectionDef[]; workflows?: WorkflowDef[]; children: ReactNode }) {
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [version, setVersion] = useState(0);
  return (
    <RuntimeCtx.Provider
      value={{
        collections,
        workflows,
        selected,
        select: (b, r) => setSelected((s) => ({ ...s, [b]: r })),
        version,
        changed: () => setVersion((v) => v + 1),
      }}
    >
      {children}
    </RuntimeCtx.Provider>
  );
}

type B<T extends BlockDef["type"]> = Extract<BlockDef, { type: T }>;

export function BlockView({ block }: { block: BlockDef }) {
  const rt = useRuntime();
  if (block.type === "text") return <div className="txt">{block.content}</div>;
  const c = rt.collections.find((x) => x.name === block.collection);
  if (!c) return <p className="hint">A coleção "{block.collection}" não existe, ou o seu papel não dá acesso a ela.</p>;
  if (block.type !== "form" && c.access && !c.access.read) return <p className="hint">Seu papel não permite ver registros de "{c.label}".</p>;
  switch (block.type) {
    case "table":
      return <TableBlock b={block} c={c} />;
    case "form":
      return <FormBlock b={block} c={c} />;
    case "details":
      return <DetailsBlock b={block} c={c} />;
    case "metric":
      return <MetricBlock b={block} c={c} />;
    case "kanban":
      return <KanbanBlock b={block} c={c} />;
  }
}

const fieldsOf = (c: CollectionDef, names: string[] | undefined) =>
  (names ?? c.fields.map((f) => f.name)).map((n) => c.fields.find((f) => f.name === n)).filter((f): f is FieldDef => !!f);

function Value({ f, r }: { f: FieldDef; r: RecordView }) {
  const v = r.values[f.name];
  if (f.interface === "attachment") {
    const list = (Array.isArray(v) ? v : []) as FileRef[];
    if (!list.length) return <span className="muted">—</span>;
    return (
      <span className="row" style={{ gap: 4 }}>
        {list.map((x) => (
          <FileChip key={x.id} f={x} />
        ))}
      </span>
    );
  }
  if ((f.interface === "select" || f.interface === "multiSelect") && v) {
    const list = Array.isArray(v) ? (v as string[]) : [String(v)];
    return (
      <span className="row" style={{ gap: 4 }}>
        {list.map((x) => (
          <span key={x} className={`chip ${choiceColor(f, x)}`}>
            {formatValue(f, x)}
          </span>
        ))}
      </span>
    );
  }
  const text = formatValue(f, v, r.related?.[f.name]);
  return text ? <>{text}</> : <span className="muted">—</span>;
}

function useRecords(c: CollectionDef, opts: { filter?: unknown; sort?: string[]; limit: number; cursor?: string | null; expand?: string[] }) {
  const rt = useRuntime();
  const [page, setPage] = useState<ListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(opts);
  useEffect(() => {
    let live = true;
    get<ListResult>(`/v1/collections/${c.name}/records${qs({ ...opts, count: true })}`)
      .then((p) => live && (setPage(p), setError(null)))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.name, key, rt.version]);
  return { page, error };
}

function TableBlock({ b, c }: { b: B<"table">; c: CollectionDef }) {
  const rt = useRuntime();
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cols = fieldsOf(c, b.columns);
  const expand = cols.filter((f) => f.interface === "relation").map((f) => f.name);
  const { page, error } = useRecords(c, { filter: b.filter, sort: b.sort, limit: b.pageSize ?? 10, cursor: cursors.at(-1), expand });
  useEffect(() => setCursors([null]), [JSON.stringify(b.filter), JSON.stringify(b.sort), b.pageSize]);
  return (
    <div>
      {b.title && <h3>{b.title}</h3>}
      {error && <p className="error">{error}</p>}
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              {cols.map((f) => (
                <th key={f.name} className={isNumeric(f) ? "num" : ""}>
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {page?.records.map((r) => (
              <tr key={r.id} className={`click ${rt.selected[b.id] === r.id ? "cur" : ""}`} onClick={() => rt.select(b.id, r.id)}>
                {cols.map((f) => (
                  <td key={f.name} className={isNumeric(f) ? "num" : ""}>
                    <Value f={f} r={r} />
                  </td>
                ))}
              </tr>
            ))}
            {page && page.records.length === 0 && (
              <tr>
                <td colSpan={cols.length || 1} className="muted">
                  Nenhum registro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pager">
        <span>{page?.total ?? "…"} registros</span>
        <span className="row">
          <button className="btn sm" disabled={cursors.length < 2} onClick={() => setCursors((s) => s.slice(0, -1))}>
            Anterior
          </button>
          <span>página {cursors.length}</span>
          <button className="btn sm" disabled={!page?.nextCursor} onClick={() => setCursors((s) => [...s, page!.nextCursor])}>
            Próxima
          </button>
        </span>
      </div>
    </div>
  );
}

function DetailsBlock({ b, c }: { b: B<"details">; c: CollectionDef }) {
  const rt = useRuntime();
  const picked = b.source ? rt.selected[b.source] : undefined;
  const fields = fieldsOf(c, b.fields);
  const [rec, setRec] = useState<RecordView | null>(null);
  const [state, setState] = useState<"loading" | "none" | "ok">("loading");
  const expand = fields.filter((f) => f.interface === "relation").map((f) => f.name).join(",");
  useEffect(() => {
    let live = true;
    const load = picked
      ? get<{ record: RecordView }>(`/v1/collections/${c.name}/records/${picked}${qs({ expand })}`).then((r) => r.record)
      : get<ListResult>(`/v1/collections/${c.name}/records${qs({ limit: 1, expand })}`).then((p) => p.records[0] ?? null);
    load
      .then((r) => live && (setRec(r), setState(r ? "ok" : "none")))
      .catch(() => live && setState("none"));
    return () => {
      live = false;
    };
  }, [c.name, picked, expand, rt.version]);
  return (
    <div>
      {b.title && <h3>{b.title}</h3>}
      {state === "none" && <p className="hint">Nenhum registro.</p>}
      {rec && (
        <dl className="dl">
          {fields.map((f) => (
            <div key={f.name} style={{ display: "contents" }}>
              <dt>{f.label}</dt>
              <dd>
                <Value f={f} r={rec} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {rec && <RecordActions c={c} id={rec.id} />}
      {!b.source && <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>Mostra o registro mais antigo; ligue a uma tabela para mostrar o clicado.</p>}
    </div>
  );
}

/** Buttons for the manual workflows of this collection the person may run. */
function RecordActions({ c, id }: { c: CollectionDef; id: string }) {
  const rt = useRuntime();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);
  const list = rt.workflows.filter((w) => w.enabled && w.trigger.type === "manual" && w.trigger.collection === c.name);
  if (!list.length) return null;
  async function run(w: WorkflowDef) {
    setBusy(w.name);
    setMsg(null);
    try {
      const { run } = await api<{ run: WorkflowRun }>("POST", `/v1/workflows/${w.name}/run`, { recordId: id });
      setMsg(run.status === "ok" ? { ok: `${w.label}: feito.` } : { err: run.error ?? "Falhou." });
      rt.changed();
    } catch (e) {
      setMsg({ err: e instanceof ApiError ? e.message : "Não foi possível rodar." });
    } finally {
      setBusy(null);
    }
  }
  return (
    <div className="row" style={{ marginTop: 10, flexWrap: "wrap", gap: 6 }}>
      {list.map((w) => (
        <button key={w.name} className="btn sm" disabled={!!busy} onClick={() => run(w)} title={w.description ?? undefined}>
          {(w.trigger.type === "manual" && w.trigger.label) || w.label}
        </button>
      ))}
      {msg?.ok && <span className="muted" style={{ fontSize: 12.5 }}>{msg.ok}</span>}
      {msg?.err && <span className="error" style={{ fontSize: 12.5 }}>{msg.err}</span>}
    </div>
  );
}

function FormBlock({ b, c }: { b: B<"form">; c: CollectionDef }) {
  const rt = useRuntime();
  // Only what this person may write: read-only fields for their role stay out of the form.
  const fields = fieldsOf(c, b.fields).filter((f) => !c.access?.readOnly.includes(f.name));
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const clean = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && !v.length)));
    try {
      await api("POST", `/v1/collections/${c.name}/records`, { values: clean });
      setValues({});
      setErrors({});
      setMsg({ ok: "Registro criado." });
      rt.changed();
    } catch (err) {
      if (err instanceof ApiError) {
        const byField: Record<string, string> = {};
        for (const d of err.details) if (d.path[0] === "values" && typeof d.path[1] === "string") byField[d.path[1]] = d.message;
        setErrors(byField);
        const outside = err.details.filter((d) => !(typeof d.path[1] === "string" && fields.some((f) => f.name === d.path[1])));
        setMsg({ err: outside.length ? outside.map((d) => `${String(d.path.at(-1) ?? "")}: ${d.message}`).join("; ") : err.message });
      } else setMsg({ err: "Não foi possível salvar." });
    } finally {
      setBusy(false);
    }
  }

  if (c.access && !c.access.create) return <p className="hint">Seu papel não permite criar registros em "{c.label}".</p>;
  return (
    <form onSubmit={submit}>
      {b.title && <h3>{b.title}</h3>}
      <div className="form">
        {fields.map((f) => {
          const id = `${b.id}-${f.name}`;
          return (
            <label key={f.name} className={`fld ${errors[f.name] ? "bad" : ""} ${f.interface === "longText" ? "wide" : ""}`} htmlFor={id}>
              {f.label}
              {f.options.required ? " *" : ""}
              <FieldInput id={id} field={f} collection={c.name} value={values[f.name]} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} />
              {errors[f.name] && <span className="err">{errors[f.name]}</span>}
            </label>
          );
        })}
      </div>
      <div className="form-foot">
        <button className="btn primary" type="submit" disabled={busy}>
          {b.submitLabel ?? "Salvar"}
        </button>
        {msg?.ok && <span className="ok">{msg.ok}</span>}
        {msg?.err && <span className="error">{msg.err}</span>}
      </div>
    </form>
  );
}

const AGG_TEXT = { count: "", sum: "soma de", avg: "média de", min: "mínimo de", max: "máximo de" } as const;

function MetricBlock({ b, c }: { b: B<"metric">; c: CollectionDef }) {
  const rt = useRuntime();
  const [value, setValue] = useState<number | null | undefined>(undefined);
  const agg = b.agg ?? "count";
  const f = b.field ? c.fields.find((x) => x.name === b.field) : undefined;
  useEffect(() => {
    let live = true;
    get<{ value: number | null }>(`/v1/collections/${c.name}/aggregate${qs({ agg, field: b.field, filter: b.filter })}`)
      .then((r) => live && setValue(r.value))
      .catch(() => live && setValue(null));
    return () => {
      live = false;
    };
  }, [c.name, agg, b.field, JSON.stringify(b.filter), rt.version]);
  return (
    <div className="metric">
      <div className="s">{b.title ?? c.label}</div>
      <div className="v">{value === undefined ? "…" : agg === "count" ? formatNumber(undefined, value) : formatNumber(f, value)}</div>
      <div className="s">{agg === "count" ? `${c.label.toLowerCase()}${b.filter ? " no filtro" : ""}` : `${AGG_TEXT[agg]} ${f?.label.toLowerCase() ?? b.field}`}</div>
    </div>
  );
}

function KanbanBlock({ b, c }: { b: B<"kanban">; c: CollectionDef }) {
  const rt = useRuntime();
  const g = c.fields.find((f) => f.name === b.groupBy);
  const t = c.fields.find((f) => f.name === (b.cardTitle ?? c.titleField)) ?? c.fields[0];
  const { page, error } = useRecords(c, { limit: 200 });
  const [over, setOver] = useState<string | null>(null);
  const [moveErr, setMoveErr] = useState<string | null>(null);
  if (!g) return <p className="hint">O campo "{b.groupBy}" não existe mais.</p>;
  const canMove = !c.access || (c.access.update && !c.access.readOnly.includes(g.name));
  const cols = [...choicesOf(g).map((ch) => ch.value), ""];

  async function drop(recordId: string, to: string) {
    setOver(null);
    const r = page?.records.find((x) => x.id === recordId);
    if (!r || (r.values[g!.name] ?? "") === to) return;
    try {
      await api("PATCH", `/v1/collections/${c.name}/records/${recordId}`, { values: { [g!.name]: to || null }, expectedVersion: r.version });
      setMoveErr(null);
      rt.changed();
    } catch (e) {
      setMoveErr(e instanceof Error ? e.message : "Não foi possível mover");
    }
  }

  return (
    <div>
      {b.title && <h3>{b.title}</h3>}
      {(error || moveErr) && <p className="error">{error ?? moveErr}</p>}
      <div className="kan">
        {cols.map((col) => {
          const rows = (page?.records ?? []).filter((r) => (r.values[g.name] ?? "") === col);
          if (col === "" && !rows.length) return null;
          return (
            <div
              key={col || "_"}
              className={`kan-col ${over === col ? "over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(col);
              }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => drop(e.dataTransfer.getData("text/x-record"), col)}
            >
              <header>
                {col ? <span className={`chip ${choiceColor(g, col)}`}>{formatValue(g, col)}</span> : <span className="muted">Sem valor</span>}
                <span className="muted">{rows.length}</span>
              </header>
              {rows.map((r) => (
                <div key={r.id} className="kan-card" draggable={canMove} onDragStart={(e) => e.dataTransfer.setData("text/x-record", r.id)}>
                  {t ? formatValue(t, r.values[t.name]) || "—" : r.id.slice(0, 8)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
